/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { Fragment, useMemo } from 'react'
import { sumBy } from 'lodash'

import { Tooltip } from '@elastic/charts'
import { EuiFlexGroup, EuiFlexItem, EuiSpacer } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import useFilters from '../../Filters/useFilters'

import { getPartialMonthSelections } from './utils'

const ChartTooltip = () => {
  const { filters } = useFilters()

  const partialMonths = useMemo(
    () =>
      getPartialMonthSelections({
        startDate: filters.startDate,
        endDate: filters.endDate,
        viewBy: filters.viewBy,
      }),
    [filters.endDate, filters.startDate, filters.viewBy],
  )

  return (
    <Tooltip
      header={({ header, items }) => {
        if (!header) {
          return null
        }

        const total = sumBy(items, 'value')

        if (partialMonths.includes(header.formattedValue) && total) {
          return (
            <Fragment>
              <p>{header.formattedValue}</p>
              <EuiSpacer size='xs' />
              <FormattedMessage
                id='costs-chart-tooltip.partial-data'
                defaultMessage='The selected time range does not include this entire bucket. {br}It might contain partial data.'
                values={{
                  br: <br />,
                }}
              />
            </Fragment>
          )
        }

        return <p>{header.formattedValue}</p>
      }}
      footer={({ items }) => {
        const total = sumBy(items, 'value')

        return (
          <EuiFlexGroup>
            <EuiFlexItem>
              <FormattedMessage id='costs-chart-tooltip-total' defaultMessage='Total' />
            </EuiFlexItem>
            <EuiFlexItem grow={false}>{total.toFixed(4)}</EuiFlexItem>
          </EuiFlexGroup>
        )
      }}
    />
  )
}

export default ChartTooltip
